import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  Color,
  FontStyle,
  defaultBottomPadding,
  defaultInnerStyle,
  defaultContainerStyle,
  ApplicationInputStyle,
  SurveyButtonStyle,
  Inputs,
} from "../../constants";
import CheckButton from "../CheckButton";
import HeartButton from "../HeartButton";
import TempDisplay from "../TempDisplay";
import { CustomPaper, StyledTextField } from "../AutocompleteUtils";
import { useDispatch, useSelector } from "react-redux";
import * as apiService from "../../service/ApiService";
import { isLoggedIn } from "../../utils/utils";
import FettiFileUpload from "../FettiFileUpload";
import FettiInput from "../FettiInput";
import ResumeSelect from "../ApplyPanels/ResumeSelect";
import NewResumeButton from "../ApplyPanels/NewResumeButton";
import ResumePreview from "../ResumeComponents/ResumePreview";
import SchoolEditor from "../SchoolComponents/SchoolEditor";
import SingleSchoolPanel from "../SchoolComponents/SingleSchoolPanel";
import AddSchoolButton from "../SchoolComponents/AddSchoolButton";

const styles = {
  container: {
    ...defaultContainerStyle,
  },
  inner: {
    ...defaultInnerStyle,
    "& .MuiFormControl-root": {
      border: "none",
    },
    px: { xs: "0.5rem", sm: 0 },
    paddingBottom: "16px",
  },
  subtitle: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "center",
  },
  link: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "center",
    textDecoration: "underline",
    cursor: "pointer",
    color: "black",
  },
  body: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "left",
    lineHeight: "1.4",
    maxWidth: "500px",
    "& > * >a": {
      color: "#000",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "1rem",
    // textAlign: 'center',
    lineHeight: "1.4",
    width: "100%",
    maxWidth: "500px",
  },
  search: {
    width: "100%",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  searchProps: {
    disableUnderline: true,
  },
  info: {
    borderLeft: `6px solid ${Color.primary}`,
    borderRadius: "0.75rem",
    backgroundColor: "#fff",
    padding: "1.2rem",
  },
};
const defaultEditData = {
  school_name: "",
  degree: "",
  major: "",
  start_month: "",
  start_year: "",
  end_month: "",
  end_year: "",
  grade: "",
  activities: "",
};

const Q_Education = ({
  selected,
  setSelected,
  setQuestionsDone,
  questionNum,
  currentQuestionNum,
  options,
  setRefreshCount,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const loggedIn = isLoggedIn(auth);
  const apply = useSelector((state) => state.apply);
  const survey = useSelector((state) => state.survey);
  const schoolIds = survey.schoolIds;
  const schoolData = survey.schoolData;

  const [loadedSchools, setLoadedSchools] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [schoolId, setSchoolId] = React.useState(null);
  const [userClickedEdit, setUserClickedEdit] = React.useState(false);
  const [editingData, setEditingData] = React.useState({ ...defaultEditData });
  const showSchoolEditor = userClickedEdit || schoolIds.length === 0;

  useEffect(() => {
    setRefreshCount((c) => c + 1);
  }, [schoolData, loadedSchools, showSchoolEditor]);

  useEffect(() => {
    // load school list when entering this page
    if (!loggedIn) return;
    if (loadedSchools) return;
    if (currentQuestionNum !== questionNum) return;

    // this quesiton is optional, so always set done
    setQuestionsDone((prev) => ({ ...prev, [questionNum]: true }));

    console.log("loading school data", schoolIds);
    apiService.getSchools(
      dispatch,
      null,
      { ...auth.accountData },
      {
        handleSuccess: () => {
          setLoadedSchools(true);
        },
      }
    );
  }, [loggedIn, loadedSchools, questionNum, currentQuestionNum]);
  useEffect(() => {
    // save when going to next panel, if school info exists
    if (!loggedIn) return;
    if (currentQuestionNum !== questionNum + 1 || !showSchoolEditor) return;
    if (!editingData.school_name) return;
    handleSave();
  }, [currentQuestionNum]);

  const handleBeginEdit = (newSchoolId) => {
    setSchoolId(newSchoolId);
    setUserClickedEdit(true);
    console.log("edit", schoolData[newSchoolId]);
    setEditingData({
      ...defaultEditData,
      ...schoolData[newSchoolId],
    });
  };
  const handleCancel = () => {
    setUserClickedEdit(false);
    setSchoolId(null);
    setEditingData({ ...defaultEditData });
  };

  const handleSave = () => {
    if (!editingData.school_name) {
      return;
    }
    setUploading(true);
    if (schoolId) {
      // updating an existing school
      apiService.updateSchool(
        dispatch,
        null,
        {
          ...auth.accountData,
          schoolId: schoolId,
          schoolData: editingData,
        },
        {
          handleSuccess: () => {
            setUploading(false);
            setUserClickedEdit(false);
            // todo: dispatch action to update schoolData
          },
        }
      );
    } else {
      // creating a new school
      apiService.addSchool(
        dispatch,
        null,
        {
          ...auth.accountData,
          schoolData: editingData,
        },
        {
          handleSuccess: () => {
            setUploading(false);
            setUserClickedEdit(false);
            // todo: dispatch action to update schoolData
          },
        }
      );
    }
  };
  const handleDelete = (deleteSchoolId) => {
    setUploading(true);
    apiService.deleteSchool(
      dispatch,
      null,
      {
        ...auth.accountData,
        schoolId: deleteSchoolId,
      },
      {
        handleSuccess: () => {
          setUploading(false);
          setUserClickedEdit(false);
          // todo: dispatch action to update schoolData
        },
      }
    );
  };

  // set done if user has an uploaded resume
  // useEffect(() => {
  //   if (selectedResume) {
  //     setQuestionsDone((prev) => ({ ...prev, [questionNum]: true }));
  //   } else {
  //     setQuestionsDone((prev) => ({ ...prev, [questionNum]: false }));
  //   }
  // }, [selectedResume]);
  return (
    <Stack
      direction="column"
      sx={styles.container}
      alignItems="center"
      spacing={2}>
      {/* <TempDisplay selected={selected} options={options} questionNum={questionNum} /> */}
      {!loggedIn && (
        // this shouldn't happen
        <Box sx={styles.subtitle}>Please log in to edit schools</Box>
      )}
      {loggedIn && !loadedSchools && <CircularProgress />}
      {loggedIn && loadedSchools && (
        <Stack
          direction="column"
          sx={styles.inner}
          alignItems="left"
          justifyContent="center"
          spacing={2}>
          {schoolIds.length > 0 && (
            <Stack direction="column" sx={{ width: "100%" }} spacing={1.5}>
              <Box sx={styles.label}>Your Education</Box>
              {schoolIds.map((schoolId, index) => {
                return (
                  <SingleSchoolPanel
                    key={index}
                    schoolId={schoolId}
                    handleDelete={handleDelete}
                    handleBeginEdit={handleBeginEdit}
                    {...schoolData[schoolId]}
                  />
                );
              })}
              {!showSchoolEditor && (
                <AddSchoolButton setOpen={setUserClickedEdit} />
              )}
            </Stack>
          )}
          {showSchoolEditor && (
            <>
              <SchoolEditor
                data={editingData}
                setData={setEditingData}
                uploading={uploading}
                handleSave={handleSave}
                handleCancel={handleCancel}
                schoolId={schoolId}
                canCancel={schoolIds.length > 0}
              />
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default Q_Education;
